import { useState } from "react";
import { ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { useScanner } from "../contexts/ScannerContext.jsx";
import { useConnectivity } from "../contexts/ConnectivityContext.jsx";
import { useWbSettings } from "../lib/useWbSettings.js";
import { useAuth } from "../contexts/AuthContext.jsx";
import { useConfirm } from "../components/ConfirmDialog.jsx";
import { useToast } from "../components/Toast.jsx";
import { api } from "../api";
import SyncConflictsPanel from "../components/SyncConflictsPanel.jsx";

const selectClass = "h-9 w-full max-w-[220px] rounded-md border border-slate-200 bg-transparent px-3 text-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-50";

function ToggleRow({ label, value, onChange }) {
  return (
    <div className="flex items-center justify-between py-2">
      <span className="text-sm text-slate-600">{label}</span>
      <Switch checked={value} onCheckedChange={onChange} />
    </div>
  );
}

function RangeRow({ label, value, onChange, min, max }) {
  return (
    <div className="flex items-center justify-between py-2">
      <span className="text-sm text-slate-600">{label}</span>
      <div className="flex items-center gap-2">
        <input
          type="range"
          min={min}
          max={max}
          value={value}
          onChange={e => onChange(Number(e.target.value))}
          className="w-32 accent-blue-600"
        />
        <span className="text-sm text-slate-500 text-right" style={{ minWidth: 32 }}>{value}</span>
      </div>
    </div>
  );
}

export default function SettingsPage({ heatSettings, updateHeat }) {
  const {
    ip, setIp, port, setPort,
    status, devices, selectedDevice, setSelectedDevice,
    resolution, setResolution,
    paperSize, setPaperSize,
    orientation, setOrientation,
    imageRotate, setImageRotate,
    error, connect, disconnect,
    resolutionOptions, paperSizeOptions,
    deviceOptions,
    source, setSource,
    colorMode, setColorMode,
    brightness, setBrightness,
    contrast, setContrast,
    quality, setQuality,
    swcrop, setSwcrop,
    swdeskew, setSwdeskew,
    removeBlankPage, setRemoveBlankPage,
    removeBlackEdges, setRemoveBlackEdges,
    denoise, setDenoise,
    borderFill, setBorderFill,
    removePunchHole, setRemovePunchHole,
    multiFeed, setMultiFeed,
    sharpen, setSharpen,
    autoRotate, setAutoRotate,
  } = useScanner();

  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [ipError, setIpError] = useState("");
  const [wbSettings, updateWb] = useWbSettings();
  const {
    online, onlineError, total, unsynced, syncDetails, syncErrors,
    serverConfigured, syncPending, recheckPending, sync, recheck, refreshStatus,
  } = useConnectivity();
  const { user } = useAuth();
  const { confirm, ConfirmDialogElement } = useConfirm();
  const { showToast, ToastElement } = useToast();
  const [resettingCatalog, setResettingCatalog] = useState(false);

  const handleSync = async () => {
    try {
      const response = await sync();
      const errors = response?.result?.errors || [];
      showToast(errors.length ? `Синхронизация не завершена: ${errors[0]}` : "Синхронизация завершена");
    } catch (err) {
      showToast(err.response?.data?.error || err.message || "Ошибка синхронизации");
    }
  };

  const handleResetCatalog = async () => {
    if (!(await confirm(
      "Локальный каталог товаров и справочников будет очищен и заново подтянут с центра. Несинхронизированные локальные изменения не пострадают — трогаются только сами справочники, не пациенты/сканы/покупки.",
      { danger: true }
    ))) return;
    setResettingCatalog(true);
    try {
      await api.resetCatalog();
      showToast("Каталог обновлён");
      setTimeout(() => window.location.reload(), 1200);
    } catch (err) {
      showToast(err.response?.data?.error || "Ошибка сброса каталога");
      setResettingCatalog(false);
    }
  };

  const sourceOptions = deviceOptions?.source?.value || ["Flatbed", "ADF-Front", "ADF-Back", "ADF-Duplex"];

  const handleConnect = () => {
    if (!/^[a-zA-Z0-9.-]+$/.test(ip.trim())) {
      setIpError("Некорректный IP/хост — разрешены только буквы, цифры, точки и дефис");
      return;
    }
    setIpError("");
    connect();
  };

  const statusDotClass = status === "connected" ? "bg-green-400" : status === "connecting" ? "bg-amber-400 animate-pulse" : "bg-red-400";
  const statusLabel = status === "connected" ? "Подключено" : status === "connecting" ? "Соединение..." : "Не подключено";

  return (
    <div className="flex flex-col flex-1 overflow-hidden">
      <div className="px-5 py-4 border-b border-slate-200 bg-white flex-shrink-0">
        <h2 className="text-lg font-bold text-slate-900">Настройки</h2>
      </div>
      <div className="flex-1 overflow-y-auto px-5 py-5 flex flex-col gap-5 max-w-xl">
        <div className="rounded-xl border border-slate-200 bg-white shadow-sm p-5">
          <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3.5">Подключение к сканеру</h3>
          <div className="flex gap-2 mb-3">
            <Input
              className="h-9 max-w-[160px]"
              value={ip}
              onChange={e => setIp(e.target.value)}
              placeholder="IP"
              disabled={status !== "disconnected"}
            />
            <Input
              className="h-9 max-w-[100px]"
              value={port}
              onChange={e => setPort(e.target.value)}
              placeholder="Port"
              disabled={status !== "disconnected"}
            />
          </div>
          <div className="mb-2.5">
            {status === "disconnected" && (
              <Button onClick={handleConnect}>Подключить</Button>
            )}
            {status === "connecting" && (
              <Button variant="ghost" disabled>Подключение...</Button>
            )}
            {status === "connected" && (
              <Button variant="ghost" onClick={disconnect}>Отключить</Button>
            )}
          </div>
          <span className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-500">
            <span className={`h-2 w-2 rounded-full ${statusDotClass}`} />
            {statusLabel}
          </span>
          {ipError && <div className="text-xs text-red-600 mt-2">{ipError}</div>}
          {error && <div className="text-xs text-red-600 mt-2">{error}</div>}
        </div>

        {serverConfigured && (
          <div className="rounded-xl border border-slate-200 bg-white shadow-sm p-5">
            <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3.5">Синхронизация с центром</h3>
            <div className="flex items-center gap-1.5 mb-3.5">
              <span className={`h-2 w-2 rounded-full ${online ? "bg-green-400" : "bg-red-400"}`} />
              <span className="text-xs font-medium text-slate-500">
                {online ? "Онлайн" : "Оффлайн"}
                {total > 0 && ` · несинхронизировано: ${total}`}
              </span>
            </div>
            {!online && onlineError && (
              <p className="text-[11px] text-red-500 -mt-2.5 mb-3.5 break-all">{onlineError}</p>
            )}
            {total > 0 && (
              <div className="mb-3.5 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                <div className="font-medium">
                  Пациенты: {unsynced.patients} · обследования: {unsynced.documents} · покупки: {unsynced.purchases}
                </div>
                {syncDetails.map(item => (
                  <div key={`${item.type}-${item.id}`} className="mt-1 break-words">
                    {item.title || item.id}: {item.reason}
                  </div>
                ))}
                {syncErrors.map((message, index) => (
                  <div key={`${index}-${message}`} className="mt-1 break-words text-red-700">{message}</div>
                ))}
              </div>
            )}
            <div className="flex gap-2">
              <Button onClick={handleSync} disabled={syncPending}>
                {syncPending ? "Синхронизация..." : "Синхронизировать"}
              </Button>
              <Button variant="ghost" onClick={() => recheck()} disabled={recheckPending}>
                {recheckPending ? "Проверка..." : "Онлайн режим"}
              </Button>
              {user?.role === "admin" && (
                <Button variant="ghost" onClick={handleResetCatalog} disabled={resettingCatalog}>
                  {resettingCatalog ? "Сброс..." : "Сбросить каталог"}
                </Button>
              )}
            </div>
            {user?.role === "admin" && !user.isCentral && (
              <SyncConflictsPanel notify={showToast} onResolved={() => { refreshStatus(); if (online) sync().catch(() => {}); }} />
            )}
          </div>
        )}

        {status === "connected" && (
          <div className="rounded-xl border border-slate-200 bg-white shadow-sm p-5">
            <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3.5">Параметры сканирования</h3>

            {devices.length > 1 && (
              <div className="flex items-center justify-between py-2">
                <span className="text-sm text-slate-600">Устройство</span>
                <select className={selectClass} value={selectedDevice} onChange={e => setSelectedDevice(e.target.value)}>
                  {devices.map(d => <option key={d.deviceName} value={d.deviceName}>{d.deviceName}</option>)}
                </select>
              </div>
            )}

            <div className="flex items-center justify-between py-2">
              <span className="text-sm text-slate-600">Формат</span>
              <select className={selectClass} value={paperSize} onChange={e => {
                const v = e.target.value;
                setPaperSize(v);
                if (v === "A3") setOrientation("portrait");
              }}>
                {paperSizeOptions.map(s => <option key={s} value={s}>{s === "auto" ? "Авто" : s}</option>)}
              </select>
            </div>

            <div className="flex items-center justify-between py-2">
              <span className="text-sm text-slate-600">Ориентация</span>
              <select className={selectClass} value={orientation} onChange={e => setOrientation(e.target.value)}>
                <option value="landscape">Горизонтальная</option>
                <option value="portrait">Вертикальная</option>
              </select>
            </div>

            <div className="flex items-center justify-between py-2">
              <span className="text-sm text-slate-600">Разрешение</span>
              <select className={selectClass} value={resolution} onChange={e => setResolution(e.target.value)}>
                {resolutionOptions.map(r => <option key={r} value={r}>{r} DPI</option>)}
              </select>
            </div>

            <div className="flex items-center justify-between py-2">
              <span className="text-sm text-slate-600">Поворот</span>
              <select className={selectClass} value={imageRotate} onChange={e => setImageRotate(Number(e.target.value))}>
                <option value={0}>0° (нет)</option>
                <option value={90}>90° по часовой</option>
                <option value={180}>180°</option>
                <option value={270}>270° (против часовой)</option>
              </select>
            </div>

            <button
              className="flex items-center gap-1.5 w-full py-2.5 mt-1 text-sm font-medium text-slate-500 hover:text-slate-700"
              onClick={() => setAdvancedOpen(o => !o)}
            >
              <ChevronDown className={`h-3.5 w-3.5 transition-transform ${advancedOpen ? "" : "-rotate-90"}`} />
              Расширенные настройки
            </button>

            {advancedOpen && (
              <div className="flex flex-col border-t border-slate-100 pt-1">
                <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide pt-3">Источник и режим</div>

                <div className="flex items-center justify-between py-2">
                  <span className="text-sm text-slate-600">Источник</span>
                  <select className={selectClass} value={source} onChange={e => setSource(e.target.value)}>
                    {sourceOptions.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>

                <div className="flex items-center justify-between py-2">
                  <span className="text-sm text-slate-600">Цветовой режим</span>
                  <select className={selectClass} value={colorMode} onChange={e => setColorMode(e.target.value)}>
                    <option value="color">Цветной</option>
                    <option value="gray">Оттенки серого</option>
                    <option value="lineart">Чёрно-белый</option>
                  </select>
                </div>

                <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide pt-3">Изображение (сканер)</div>

                <RangeRow label="Яркость" value={brightness} onChange={setBrightness} min={-100} max={100} />
                <RangeRow label="Контраст" value={contrast} onChange={setContrast} min={-100} max={100} />
                <RangeRow label="Качество JPEG" value={quality} onChange={setQuality} min={0} max={100} />

                <div className="pt-3">
                  <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Постобработка: Белый фон</div>
                  <div className="text-xs text-slate-400 mt-1 pb-1">
                    Применяется после сканирования — убирает тёмный фон, подсвечивает стопу.
                  </div>
                </div>
                <RangeRow label="Порог (lum)" value={wbSettings.threshold} onChange={v => updateWb({ threshold: v })} min={5} max={80} />
                <RangeRow label="Яркость" value={wbSettings.brightness} onChange={v => updateWb({ brightness: v })} min={-100} max={100} />
                <RangeRow label="Контраст" value={wbSettings.contrast} onChange={v => updateWb({ contrast: v })} min={-100} max={100} />

                {heatSettings && updateHeat && (<>
                  <div className="pt-3">
                    <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Постобработка: Тепловая карта</div>
                    <div className="text-xs text-slate-400 mt-1 pb-1">
                      Настройки вида «Тепловая» в разделе Сканер.
                    </div>
                  </div>
                  <div className="flex items-center justify-between py-2">
                    <span className="text-sm text-slate-600">Прозрачность</span>
                    <div className="flex items-center gap-2">
                      <input type="range" min={50} max={100} value={Math.round(heatSettings.opacity * 100)}
                        onChange={e => updateHeat({ opacity: Number(e.target.value) / 100 })} className="w-32 accent-blue-600" />
                      <span className="text-sm text-slate-500 text-right" style={{ minWidth: 36 }}>{Math.round(heatSettings.opacity * 100)}%</span>
                    </div>
                  </div>
                  <RangeRow label="Lum фона" value={heatSettings.bgThreshold} onChange={v => updateHeat({ bgThreshold: v })} min={0} max={80} />
                  <div className="flex items-center justify-between py-2">
                    <span className="text-sm text-slate-600">Инверсия</span>
                    <Switch checked={heatSettings.invertMapping} onCheckedChange={v => updateHeat({ invertMapping: v })} />
                  </div>
                  <div className="flex items-center justify-between py-2">
                    <span className="text-sm text-slate-600">Палитра</span>
                    <select className={selectClass} value={heatSettings.palette} onChange={e => updateHeat({ palette: e.target.value })}>
                      <option value="classic">Классика</option>
                      <option value="hot">Горячая</option>
                      <option value="cool">Холодная</option>
                      <option value="grey">Серая</option>
                    </select>
                  </div>
                  <div className="flex items-center justify-between py-2">
                    <span className="text-sm text-slate-600">Фон карты</span>
                    <select className={selectClass} value={heatSettings.outputBg} onChange={e => updateHeat({ outputBg: e.target.value })}>
                      <option value="white">Белый</option>
                      <option value="dark">Тёмный</option>
                    </select>
                  </div>
                </>)}

                <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide pt-3">Обработка</div>

                <ToggleRow label="Автообрезка" value={swcrop} onChange={setSwcrop} />
                <ToggleRow label="Выравнивание (deskew)" value={swdeskew} onChange={setSwdeskew} />
                <ToggleRow label="Удалить пустые страницы" value={removeBlankPage} onChange={setRemoveBlankPage} />
                <ToggleRow label="Удалить чёрные края" value={removeBlackEdges} onChange={setRemoveBlackEdges} />
                <ToggleRow label="Шумоподавление" value={denoise} onChange={setDenoise} />
                <ToggleRow label="Заполнение краёв" value={borderFill} onChange={setBorderFill} />
                <ToggleRow label="Удалить отверстия перфорации" value={removePunchHole} onChange={setRemovePunchHole} />
                <ToggleRow label="Обнаружение двойной подачи" value={multiFeed} onChange={setMultiFeed} />
                <ToggleRow label="Повышение резкости" value={sharpen} onChange={setSharpen} />
                <ToggleRow label="Автоповорот" value={autoRotate} onChange={setAutoRotate} />
              </div>
            )}
          </div>
        )}
      </div>
      <ConfirmDialogElement />
      <ToastElement />
    </div>
  );
}
